import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Camera, ChevronLeft, Upload, Trash2, Image, Loader2, X, Check, Grid, List
} from 'lucide-react';
import { toast } from 'sonner';

const photoCategories = [
  { id: 'front', label: 'Front View' },
  { id: 'rear', label: 'Rear View' },
  { id: 'left_side', label: 'Left Side' },
  { id: 'right_side', label: 'Right Side' },
  { id: 'interior_front', label: 'Interior (Front)' },
  { id: 'interior_rear', label: 'Interior (Rear)' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'odometer', label: 'Odometer' },
  { id: 'vin_plate', label: 'VIN Plate' },
  { id: 'damage', label: 'Damage Documentation' },
  { id: 'other', label: 'Other' },
];

const PhotoUploadPage = () => {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('front');
  const [caption, setCaption] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);

  useEffect(() => {
    loadPhotos();
  }, [loadId]);

  const loadPhotos = async () => {
    try {
      const response = await api.get(`/photos/load/${loadId}`);
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewImage) {
      toast.error('Please select an image first');
      return;
    }

    setUploading(true);
    try {
      await api.post('/photos', {
        load_id: loadId,
        category: selectedCategory,
        image_data: previewImage,
        caption: caption || null,
      });

      toast.success('Photo uploaded successfully');
      setPreviewImage(null);
      setCaption('');
      fileInputRef.current.value = '';
      loadPhotos();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload photo';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      await api.delete(`/photos/${photoId}`);
      toast.success('Photo deleted');
      setPhotos(photos.filter(p => p.id !== photoId));
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const openCamera = () => {
    fileInputRef.current?.click();
  };

  const groupedPhotos = photos.reduce((acc, photo) => {
    if (!acc[photo.category]) acc[photo.category] = [];
    acc[photo.category].push(photo);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16 pb-24 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Photo Documentation</h1>
            <p className="text-sm text-gray-400">{photos.length} photos uploaded</p>
          </div>
          <div className="flex gap-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              className="w-8 h-8"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              className="w-8 h-8"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="bg-[#12151a] border-[#1a1d24] mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-[#4ADE80]">
              <Camera className="w-5 h-5" />
              <span className="font-medium">Add New Photo</span>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Preview or Camera Button */}
            {previewImage ? (
              <div className="relative">
                <img
                  src={previewImage}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-black/50"
                  onClick={() => {
                    setPreviewImage(null);
                    fileInputRef.current.value = '';
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                onClick={openCamera}
                className="w-full h-32 border-2 border-dashed border-[#1a1d24] rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-[#4ADE80] hover:text-[#4ADE80] transition-colors"
              >
                <Camera className="w-8 h-8 mb-2" />
                <span className="text-sm">Tap to take photo or select from gallery</span>
              </button>
            )}

            {previewImage && (
              <>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="bg-[#0a0c10] border-[#1a1d24]">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12151a] border-[#1a1d24]">
                    {photoCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Add a caption (optional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                />

                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-[#4ADE80] text-black hover:bg-[#22C55E]"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photo
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Quick Category Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {['front', 'rear', 'left_side', 'right_side', 'damage'].map((cat) => {
            const category = photoCategories.find(c => c.id === cat);
            const count = groupedPhotos[cat]?.length || 0;
            return (
              <Button
                key={cat}
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedCategory(cat);
                  openCamera();
                }}
                className={`text-xs ${count > 0 ? 'border-green-500 text-green-400' : ''}`}
              >
                {category.label}
                {count > 0 && <Check className="w-3 h-3 ml-1" />}
              </Button>
            );
          })}
        </div>

        {/* Photos Gallery */}
        {photos.length === 0 ? (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardContent className="py-12 text-center">
              <Image className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No photos yet</p>
              <p className="text-sm text-gray-500">Take photos to document vehicle condition</p>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="relative aspect-square cursor-pointer group"
                onClick={() => setFullscreenPhoto(photo)}
              >
                <img
                  src={photo.image_data}
                  alt={photo.caption || photo.category}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <span className="text-xs text-white">{photo.category.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(groupedPhotos).map(([category, categoryPhotos]) => (
              <div key={category}>
                <Label className="text-xs text-gray-500 uppercase mb-2 block">
                  {photoCategories.find(c => c.id === category)?.label || category}
                </Label>
                {categoryPhotos.map((photo) => (
                  <Card key={photo.id} className="bg-[#12151a] border-[#1a1d24] mb-2">
                    <CardContent className="p-3 flex items-center gap-3">
                      <img
                        src={photo.image_data}
                        alt={photo.caption || category}
                        className="w-16 h-16 object-cover rounded"
                        onClick={() => setFullscreenPhoto(photo)}
                      />
                      <div className="flex-1">
                        <p className="text-sm text-white">{photo.caption || 'No caption'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(photo.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(photo.id)}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Fullscreen Photo Modal */}
        {fullscreenPhoto && (
          <div
            className="fixed inset-0 bg-black z-50 flex items-center justify-center"
            onClick={() => setFullscreenPhoto(null)}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white"
              onClick={() => setFullscreenPhoto(null)}
            >
              <X className="w-6 h-6" />
            </Button>
            <img
              src={fullscreenPhoto.image_data}
              alt={fullscreenPhoto.caption}
              className="max-w-full max-h-full object-contain"
            />
            {fullscreenPhoto.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4">
                <p className="text-white text-center">{fullscreenPhoto.caption}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoUploadPage;
